import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { twMerge } from "tailwind-merge"
import Input from "aldoo-ra/Input"

const resolveSortFunction = (sortBy, sortDirection) => {
  if (!sortBy) {
    return () => 0
  }

  return (a, b) => {
    if (a[sortBy] < b[sortBy]) {
      return sortDirection === "asc" ? -1 : 1
    }
    if (a[sortBy] > b[sortBy]) {
      return sortDirection === "asc" ? 1 : -1
    }
    return 0
  }
}

// Theme-token map per variant. `admin` keeps the historical look; `site` uses
// the site theme tokens so the dropdown respects light/dark themes outside
// the admin shell.
const VARIANT_STYLES = {
  admin: {
    trigger:
      "inline-flex justify-between w-full rounded-md border border-admin_bordered px-4 py-2 bg-admin_paper text-sm font-medium text-admin_text outline-none focus:outline-none focus:border-admin_primary focus:shadow-none",
    menu: "absolute z-10 mt-2 rounded-md shadow-lg bg-admin_paper ring-1 ring-black ring-opacity-5",
    searchWrap: "p-2 border-b border-admin_neutral_2",
    searchInputVariant: "admin",
    searchInputCls:
      "w-full px-3 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-admin_primary",
    optionBase:
      "px-4 py-2 text-sm cursor-pointer hover:bg-admin_neutral_1 hover:text-text flex justify-between items-center",
    optionSelected: "bg-admin_paper text-admin_text",
    optionDefault: "bg-admin_neutral text-admin_text_secondary",
    checkIcon: "ml-2 h-5 w-5 text-admin_primary",
  },
  site: {
    trigger:
      "inline-flex justify-between w-full rounded-md border border-bordered px-4 py-2 bg-paper text-sm font-medium text-text outline-none focus:outline-none focus:border-primary focus:shadow-none",
    menu: "absolute z-10 mt-2 rounded-md shadow-lg bg-paper border border-bordered",
    searchWrap: "p-2 border-b border-bordered",
    searchInputVariant: "default",
    searchInputCls:
      "w-full px-3 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary",
    optionBase:
      "px-4 py-2 text-sm cursor-pointer hover:bg-neutral_1 flex justify-between items-center",
    optionSelected: "bg-paper text-text",
    optionDefault: "bg-neutral text-text_secondary",
    checkIcon: "ml-2 h-5 w-5 text-primary",
  },
}

const Dropdown = ({
  className = "",
  options,
  value,
  onChange,
  menuPosition = "bottom",
  onItemRender,
  markSelected = true,
  sortBy,
  sortDirection = "asc",
  placeholder,
  isMenu = false,
  searchEnabled = false,
  variant = "admin",
}) => {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.admin
  const [selectedOption, setSelectedOption] = useState(
    !placeholder && (value || options[0])
  )
  const [isOpen, setIsOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)
  const searchInputRef = useRef(null)

  useEffect(() => {
    if (value) {
      setSelectedOption(value)
    }
  }, [value])

  const toggleDropdown = () => {
    if (!isOpen) {
      setIsOpen(true)
      if (searchEnabled) {
        setShowSearch(true)
        // Focus the search input after rendering
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus()
          }
        }, 0)
      }
    } else {
      setIsOpen(false)
      setShowSearch(false)
      setSearchQuery("")
    }
  }

  const handleOptionClick = (option) => {
    if (!isMenu) setSelectedOption(option)
    setIsOpen(false)
    setShowSearch(false)
    setSearchQuery("")
    if (onChange) onChange(option)
  }

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options
    return options.filter((option) => {
      if (typeof option === "string") {
        return option.toLowerCase().includes(searchQuery.toLowerCase())
      }
      // If onItemRender is provided, we'll try to match against the rendered string
      const searchValue = onItemRender
        ? String(onItemRender(option)).toLowerCase()
        : String(option).toLowerCase()
      return searchValue.includes(searchQuery.toLowerCase())
    })
  }, [options, searchQuery, onItemRender])

  const getMenuPositionStyles = useCallback(() => {
    if (!buttonRef.current || !dropdownRef.current) return {}

    const buttonRect = buttonRef.current.getBoundingClientRect()

    const styles = {
      width: `${buttonRect.width}px`,
    }

    switch (menuPosition) {
      case "top":
        styles.bottom = `${buttonRect.height}px`
        styles.left = `${0}px`
        break
      case "bottom":
        styles.top = `${buttonRect.height}px`
        styles.left = `${0}px`
        break
      default:
        styles.top = `${buttonRect.height}px`
        styles.left = `${0}px`
    }

    return styles
  }, [menuPosition])

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const positionStyles = getMenuPositionStyles()
      Object.assign(dropdownRef.current.style, positionStyles)
    }
  }, [isOpen, getMenuPositionStyles])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false)
        setShowSearch(false)
        setSearchQuery("")
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    } else {
      document.removeEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className={twMerge("relative inline-block text-left", className)}>
      <div>
        <button
          ref={buttonRef}
          type="button"
          className={v.trigger}
          id="options-menu"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={toggleDropdown}
        >
          {!selectedOption && <div className="">{placeholder}</div>}
          {selectedOption && onItemRender
            ? onItemRender(selectedOption)
            : selectedOption}
          <svg
            className="-mr-1 ml-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className={v.menu}
        >
          {searchEnabled && showSearch && (
            <div className={v.searchWrap}>
              <Input
                variant={v.searchInputVariant}
                ref={searchInputRef}
                type="text"
                className={v.searchInputCls}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="rounded-lg overflow-hidden">
            {filteredOptions
              .sort(resolveSortFunction(sortBy, sortDirection))
              .map((option, index) => (
                <div
                  key={index}
                  className={twMerge(
                    v.optionBase,
                    option === selectedOption ? v.optionSelected : v.optionDefault
                  )}
                  role="menuitem"
                  onClick={() => handleOptionClick(option)}
                >
                  {onItemRender ? onItemRender(option) : option}
                  {option === selectedOption && markSelected && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className={v.checkIcon}
                      fill="currentColor"
                      width="24px"
                      height="24px"
                    >
                      <path d="M0 0h24v24H0z" fill="none" />
                      <path d="M9 16.2l-3.5-3.5L4 14l5 5L20 8.5l-1.5-1.5z" />
                    </svg>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dropdown
